<template>
  <v-app>
    <LanguageSelectionPage
      v-if="showLanguageSelection"
      class="LanguageSelectionPage"
      @language-selected="onLanguageSelected"
    />
    <PlaylistCarousel v-if="!loading && displayPlaylistScreen" class="full-screen" /> 
    <v-container>
      <app-head 
        :aria-label="t('app.appHead')"
        class="app-head" 
        role="presentation"
        :stopDescription="stopDescription"
        :multiStop="multiStop"
        />
      <vue-cookie-accept-decline
        elementId="cookiepreference"
        position="bottom-right"
        ref="cookiepreference"
        transitionName="slideFromBottom"
        type="floating"
        :debug="false"
        :disableDecline="false"
        :showPostponeButton="true"
        @clicked-accept="cookieClickedAccept"
        @clicked-decline="cookieClickedDecline"
      >
        <template #message>
          <div class="text-primary">
            {{ t('cookies.message') }}
          </div>
          <a :href="cookieHref" target="_blank">{{ t('cookies.learnMore') }}...</a>
        </template>
        <template #declineContent>{{ t('cookies.decline') }}</template>
        <template #acceptContent>{{ t('cookies.agree') }}</template>
      </vue-cookie-accept-decline>
      <time-table v-if="currentPage === 'timetable'" class="time-table" />
      <nav-drawer
        v-if="!disableMenu"
        @showCookieSettings="showCookieSettings"
      />
      <message-page
        v-if="currentPage === 'messages'"
        class="message-page"
        :latestMessageOnly="false"
      />
      <links-page v-if="currentPage === 'links'" class="links-page" />
    </v-container>
    <app-footer 
      v-if="!displayPlaylistScreen" 
      class="app-footer" 
      :messageType="messageType" 
    />
  </v-app>
</template>

<script>
import NavDrawer from "./components/NavDrawer.vue";
import AppHead from "./components/AppHead.vue";
import PlaylistCarousel from "./components/PlaylistCarousel.vue";
import TimeTable from "./components/TimeTable.vue";
import MessagePage from "./components/MessagePage.vue";
import LinksPage from "./components/LinksPage.vue";
import AppFooter from "./components/AppFooter.vue";
import LanguageSelectionPage from "./components/LanguageSelectionPage.vue";
import VueCookieAcceptDecline from "vue-cookie-accept-decline";
import { MessageLocation } from "../constants";
import { useI18n } from 'vue-i18n';
import { getCookie } from "../cookies";

import { mapState, mapMutations, mapGetters, mapActions } from "vuex";

export default {
  name: "App", 
  setup() {
    const { t } = useI18n();
    return { t };
  },
  components: {
    NavDrawer,
    AppHead,
    PlaylistCarousel,
    TimeTable,
    MessagePage,
    LinksPage,
    AppFooter,
    VueCookieAcceptDecline,
    LanguageSelectionPage
  },
  data() {
    return {
      loading: true,
      showLanguageSelection: false,
    };
  },
  computed: {
    ...mapGetters([
      "cookiePreference",
      "getmessagesForQRCode",
      "displayPlaylistScreen",
      "getHighestPriorityMessage",
      "getDigitalCodeDescription"
    ]),
    ...mapState([
      "qRCode",
      "currentPage",
      "disableMenu",
      "stopDescription",
      "multiStop",
      "pageTitle",
      "playlist",
      "selectedLanguage",
    ]),

    messageType() {
      return this.getHighestPriorityMessage?.type ?? MessageLocation.FOOTER;
    },
    documentTitle() {
      return this.pageTitle || this.t('app.departures');
    },
    cookieHref() {
      return this.selectedLanguage === 'cy'
        ? '/cookies-cy.html'
        : '/cookies.html';
    },
  },
  methods: {
    ...mapMutations(["recreateAllCookies", "deleteAllCookies", "setQRCode"]),
    ...mapActions(["updateRepressServices", "updateCurrentPage", "fetchData", "setIsHealthCheckRequest", "detectNetworkQuality"]),
    onLanguageSelected() {
      this.showLanguageSelection = false;
    },
    showCookieSettings() {
      this.$refs.cookiepreference.removeCookie();
      this.$refs.cookiepreference.init();
    },
    cookieClickedAccept() {
      this.recreateAllCookies();
    },
    cookieClickedDecline() {
      this.deleteAllCookies();
    },
    getIsHealthCheckRequest() {
      const healthCheckString = new URLSearchParams(window.location.search).get('healthcheck');
      const isHealthCheckRequest = healthCheckString === "true";

      return isHealthCheckRequest;    
    }
  },
  mounted() {
    this.detectNetworkQuality();
    this.updateRepressServices(false);

    this.showLanguageSelection = !getCookie("selectedLanguage");

    setTimeout(() => {
      window.scrollTo(0, 1);
    }, 0);
  },
  watch: {
    documentTitle: {
      handler(value) {
        document.title = value;
      },
      immediate: true,
    },
    // Screen readers and the browser use <html lang> for pronunciation
    selectedLanguage: {
      handler(value) {
        document.documentElement.lang = value === 'cy' ? 'cy' : 'en';
      },
      immediate: true,
    },
    $route() {
      this.setIsHealthCheckRequest(this.getIsHealthCheckRequest());     
      this.setQRCode(this.$route.params.id ?? '');

      const minSplashTime = 500; // milliseconds

      const splashTimer = new Promise(resolve => setTimeout(resolve, minSplashTime));
  
      const apiCall = this.fetchData();

      Promise.all([splashTimer, apiCall]).then(() => {
        this.loading = false;
      });
    },
  },
};
</script>

<style >
.cookie__floating__postpone-button {
  border: none;            
  padding: 0px 7px;      
  border-radius: 5px;     
  z-index: -1;
  float: right;
  font-size: 30px;
}

.app-footer {
  position: fixed !important;
  bottom: 0;
  left: 0;
  display: flex;
  width: 100%;
  flex-direction: column;
}

.information-popup {
  margin: 0px;
  width: 100%;
  overflow-y: auto;

  flex: 1;
  min-height: 5vh;
  position: relative;
}

.time-table {
  position: fixed !important;
  top: 138px ;
  max-height: 80vh;
  background-color: surface;
  border: none !important;
  border-radius: 0px;
  left: 0;
  width: 100%;
  overflow-y: auto;
}

.nav-drawer {
  position: fixed !important;
  top: 0vh;
  left: 0;
  width: 100%;
  z-index: 1011 !important;
  height: 100vh !important;
}
.app-head {
  position: fixed !important;
  top: 74px !important;
  height: 64px !important;
  min-height: 64px !important;
  left: 0;
  width: 100%;
  z-index: 1000;
}

.full-screen {
  position: fixed !important;
  top: 0vh;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 1015;
}

.message-page {
  position: fixed !important;
  top: 138px;
  max-height: 80dvh;
  background-color: surface;
  border: none !important;
  border-radius: 0px;
  left: 0;
  width: 100%;
  overflow-y: auto;
}
.links-page {
  position: fixed !important;
  top: var(--toolbar-height);
  max-height: 80dvh;
  background-color: surface;
  border: none !important;
  border-radius: 0px;
  left: 0;
  width: 100%;
  overflow-y: auto;
}
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;

  height: 101vh; /* Allow scroll to dismiss ios browser address bar*/
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.menu-app-bar{
  height: var(--app-head-top) !important;
}
.splash-screen-container {
  height:100%;
  background: rgb(var(--v-theme-splash-footer));
}

.splash-main {  
  display: flex;
  justify-content: center;  /* horizontal center */
  align-items: center;      /* vertical center */
  left: 0;
  width: 100%;
  margin: 0px !important;
  background: rgb(var(--v-theme-splash-footer)) !important;
  color: white;
  padding: 0px;
  margin: 0px !important;
}

.splash {
  max-width: 100% !important;
  background-color: rgb(var(--v-theme-splash-footer)) !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}


@media (orientation: landscape) {
  .message-page{
    max-height: 80dvh;
  }
  .links-page {
    max-height: 80dvh;
  }
}

.LanguageSelectionPage{
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 1015;
}
</style>

